import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { TeamMember } from '../types';

interface TeamManagementSectionProps {
  members: TeamMember[];
  onUpdate: (members: TeamMember[]) => void;
  isAdmin: boolean;
}

const TeamManagementSection = ({ members, onUpdate, isAdmin }: TeamManagementSectionProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showAddMember, setShowAddMember] = useState(false);
  const [showEditMember, setShowEditMember] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [formRole, setFormRole] = useState<string>('viewer');

  const roleOptions = [
    { value: 'all', label: 'Todas as Funções' },
    { value: 'admin', label: 'Administrador' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'analyst', label: 'Analista' },
    { value: 'viewer', label: 'Visualizador' }
  ];

  const statusOptions = [
    { value: 'all', label: 'Todos os Status' },
    { value: 'active', label: 'Ativo' },
    { value: 'inactive', label: 'Inativo' },
    { value: 'suspended', label: 'Suspenso' }
  ];

  const filteredMembers = members.filter((member) => {
    const matchesSearch =
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || member.role === filterRole;
    const matchesStatus = filterStatus === 'all' || member.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success border-success/20';
      case 'inactive':
        return 'bg-muted text-muted-foreground border-border';
      case 'suspended':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'inactive':
        return 'Inativo';
      case 'suspended':
        return 'Suspenso';
      default:
        return status;
    }
  };

  const handleAddMember = () => {
    setShowAddMember(true);
  };

  const handleEditMember = (member: TeamMember) => {
    setSelectedMember(member);
    setFormRole(member.role);
    setShowEditMember(true);
  };

  const handleSaveMember = (memberData: Partial<TeamMember>) => {
    if (selectedMember) {
      // Update existing member
      const updatedMembers = members.map(m =>
        m.id === selectedMember.id ? { ...m, ...memberData } : m
      );
      onUpdate(updatedMembers);
      alert('✅ Membro atualizado com sucesso!');
    } else {
      // Add new member
      const newMember: TeamMember = {
        id: Date.now().toString(),
        name: memberData.name || '',
        email: memberData.email || '',
        role: memberData.role || 'viewer',
        status: 'active',
        avatar: 'https://ui-avatars.com/api/?name=' + encodeURIComponent(memberData.name || ''),
        lastActive: new Date(),
        consultationsCount: 0
      };
      onUpdate([...members, newMember]);
      alert('✅ Membro adicionado com sucesso!');
    }
    setShowAddMember(false);
    setShowEditMember(false);
    setSelectedMember(null);
    setFormRole('viewer');
  };

  const handleSendInvites = () => {
    const activeMembers = members.filter(m => m.status === 'active');
    alert(`📧 Convites enviados para ${activeMembers.length} membros ativos!`);
  };

  const handleResetPasswords = () => {
    alert('🔑 E-mails de redefinição de senha enviados!');
  };

  const handleExportList = () => {
    const csvContent = [
      ['Nome', 'Email', 'Função', 'Status', 'Consultas', 'Último Acesso'].join(','),
      ...filteredMembers.map(m => [
        m.name,
        m.email,
        m.role,
        m.status,
        m.consultationsCount,
        new Date(m.lastActive).toLocaleDateString('pt-BR')
      ].join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `equipe_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    alert('✅ Lista de equipe exportada com sucesso!');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Gerenciamento de Equipe</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Gerencie membros da equipe e suas permissões
          </p>
        </div>
        {isAdmin && (
          <Button variant="default" iconName="UserPlus" iconPosition="left" onClick={handleAddMember}>
            Adicionar Membro
          </Button>
        )}
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Input
            type="search"
            placeholder="Buscar por nome ou e-mail..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select
            options={roleOptions}
            value={filterRole}
            onChange={(value) => setFilterRole(value as string)}
            placeholder="Filtrar por função"
          />
          <Select
            options={statusOptions}
            value={filterStatus}
            onChange={(value) => setFilterStatus(value as string)}
            placeholder="Filtrar por status"
          />
        </div>

        <div className="space-y-3">
          {filteredMembers.length > 0 ? (
            filteredMembers.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-4 bg-muted rounded-md hover:bg-muted/80 transition-colors"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="relative">
                    <Image
                      src={member.avatar}
                      alt={`Foto de perfil de ${member.name}, ${member.role} com status ${member.status}`}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    {member.status === 'active' && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success rounded-full border-2 border-card" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-foreground truncate">
                        {member.name}
                      </h4>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(
                          member.status
                        )}`}
                      >
                        {getStatusLabel(member.status)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-xs text-muted-foreground capitalize">
                        {member.role}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {member.consultationsCount} consultas
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Último acesso: {new Date(member.lastActive).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <button
                      className="p-2 hover:bg-background rounded-md transition-colors"
                      aria-label="Editar membro"
                      onClick={() => handleEditMember(member)}
                    >
                      <Icon name="Edit" size={16} color="var(--color-muted-foreground)" />
                    </button>
                    <button
                      className="p-2 hover:bg-background rounded-md transition-colors"
                      aria-label="Gerenciar permissões"
                      onClick={() => alert(`Gerenciar permissões de ${member.name}`)}
                    >
                      <Icon name="Shield" size={16} color="var(--color-muted-foreground)" />
                    </button>
                    <button
                      className="p-2 hover:bg-background rounded-md transition-colors"
                      aria-label="Mais opções"
                      onClick={() => alert(`Mais opções para ${member.name}`)}
                    >
                      <Icon name="MoreVertical" size={16} color="var(--color-muted-foreground)" />
                    </button>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <Icon
                name="Users"
                size={48}
                color="var(--color-muted-foreground)"
                className="mx-auto mb-3"
              />
              <p className="text-muted-foreground">Nenhum membro encontrado</p>
            </div>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Settings" size={20} color="var(--color-primary)" />
            Ações em Massa
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Button variant="outline" iconName="Mail" iconPosition="left" fullWidth onClick={handleSendInvites}>
              Enviar Convites
            </Button>
            <Button variant="outline" iconName="Key" iconPosition="left" fullWidth onClick={handleResetPasswords}>
              Redefinir Senhas
            </Button>
            <Button variant="outline" iconName="Download" iconPosition="left" fullWidth onClick={handleExportList}>
              Exportar Lista
            </Button>
          </div>
        </div>
      )}

      {!isAdmin && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Acesso Restrito</p>
              <p className="text-sm text-muted-foreground mt-1">
                Apenas administradores e supervisores podem gerenciar membros da equipe. Entre em
                contato com seu supervisor para alterações.
              </p>
            </div>
          </div>
        </div>
      )}

      {(showAddMember || showEditMember) && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowAddMember(false);
              setShowEditMember(false);
              setSelectedMember(null);
              setFormRole('viewer');
            }}
          />
          <div className="relative bg-card rounded-lg shadow-modal w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                {showEditMember ? 'Editar Membro' : 'Adicionar Membro'}
              </h3>
              <button
                onClick={() => {
                  setShowAddMember(false);
                  setShowEditMember(false);
                  setSelectedMember(null);
                  setFormRole('viewer');
                }}
                className="p-1 hover:bg-muted rounded"
              >
                <Icon name="X" size={20} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleSaveMember({
                  name: formData.get('name') as string,
                  email: formData.get('email') as string,
                  role: formRole
                });
              }}
              className="space-y-4"
            >
              <Input
                label="Nome Completo"
                name="name"
                defaultValue={selectedMember?.name}
                required
              />
              <Input
                label="E-mail"
                name="email"
                type="email"
                defaultValue={selectedMember?.email}
                required
              />
              <Select
                label="Função"
                name="role"
                options={roleOptions.filter(r => r.value !== 'all')}
                value={formRole}
                onChange={(value) => setFormRole(value as string)}
                required
              />
              <div className="flex gap-3 justify-end pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddMember(false);
                    setShowEditMember(false);
                    setSelectedMember(null);
                    setFormRole('viewer');
                  }}
                >
                  Cancelar
                </Button>
                <Button type="submit" variant="default">
                  {showEditMember ? 'Salvar' : 'Adicionar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagementSection;